/*
 * Copyright (c) 2024 Cozy Intellect Inc.
 * File Path: packages/toolslib/src/utils/calendar.ts
 */

import type {
  AugmentedUniqueEvents,
  CalendarEvent,
  Conversation,
  Memory,
  MemoryDay,
  ProviderCalendars,
  ProviderInfo,
  Transcript
} from '@repo/common';
import type { Schema$ConferenceData } from '@repo/common/types/GoogleCalendar';
import {
  dateComparisonStr,
  formatTime,
  getDateStr,
  getDateStrLocal,
  getDiffHours,
  parseDate
} from './date.util';
import { transcriptToMemory } from './transcript.util';

export const transformProviderCalendars = (providers: ProviderInfo[]): ProviderCalendars => {
  const result: ProviderCalendars = {};
  for (const provider of providers) {
    for (const calendar of provider.calendars) {
      result[calendar.id] = calendar;
    }
  }
  return result;
};

export const getCalendarEventUrl = (
  eventUrl: string | null | undefined,
  providerIdentifier: string | null | undefined
): string | null | undefined => {
  if (!eventUrl) return eventUrl;

  const trimmedIdentifier = providerIdentifier?.toString().trim();
  if (!trimmedIdentifier) return eventUrl;

  try {
    const url = new URL(eventUrl);
    url.searchParams.set('authuser', trimmedIdentifier);
    return url.toString();
  } catch (error) {
    const separator = eventUrl.includes('?') ? '&' : '?';
    const authUserParam = `authuser=${encodeURIComponent(trimmedIdentifier)}`;
    if (eventUrl.includes('authuser=')) {
      return eventUrl.replace(/authuser=[^&#]*/i, authUserParam);
    }
    return `${eventUrl}${separator}${authUserParam}`;
  }
};

export const transcriptToConversation = (t: Transcript): Conversation => {
  return {
    id: t.id,
    type: 'transcript',
    name: '',
    editedName: '',
    createdAt: t.createdAt || new Date(),
    updatedAt: t.updatedAt || new Date(),
    deletedAt: t?.deletedAt,
    transcriptId: t.id,
    memoryId: t.id,
    userId: t.userId
  };
};

function getCalendarDayWithEvents(
  current: Date,
  eventsByDate: Record<string, AugmentedUniqueEvents>,
  transcripts: Transcript[],
  selectedMonth: number,
  today: Date
): MemoryDay {
  const currentMonth = current.getMonth();
  const currentDateStr = dateComparisonStr(current);
  const eventsByUid = eventsByDate[currentDateStr] || {};
  const events = Object.values(eventsByUid);

  // Track which transcript IDs are already included via calendar events
  const includedTranscriptIds = new Set<string>();

  const dayEvents: Memory[] = events.map(({ event, transcript }, id) => {
    const startDate = parseDate(event.start as string | Date);
    const endDate = parseDate(event.end as string | Date);
    if (transcript?.id) {
      includedTranscriptIds.add(transcript.id);
      return transcriptToMemory(transcript, event);
    }
    // Synthesize a calendar event that is a placeholder for a future transcript.
    return {
      id: event.uid || `${id + 1}`,
      name: getCalendarTitle(event),
      time: formatTime(startDate || new Date()),
      endTime: formatTime(endDate || new Date()),
      datetime: startDate.toISOString(),
      href: '#',
      calendarEvent: event,
      workspaceId: (event as any)?.workspaceId,
      source: 'calendar',
      dateStrLocal: currentDateStr
    } as Memory;
  });

  const localDateStr = getDateStrLocal(current);

  // Only include transcripts that aren't already associated with calendar events
  const dayTranscripts: Memory[] = transcripts
    .filter((t) => {
      // Use t.date (available on both TranscriptSummary and TranscriptFull)
      // instead of t.meeting?.date (only on TranscriptFull)
      if (!t.date) return false;
      if (includedTranscriptIds.has(t.id)) return false; // Skip if already included
      return getDateStrLocal(new Date(t.date)) === localDateStr;
    })
    .map((t) => transcriptToMemory(t));

  const isToday = currentDateStr === dateComparisonStr(today);

  return {
    date: getDateStr(current) as string,
    isCurrentMonth: currentMonth === selectedMonth ? true : undefined,
    events: [...dayEvents, ...dayTranscripts] as Memory[],
    isToday: isToday
  };
}

/*
Computes all the events by calendar date.
Expensive operation.
 */
export async function getCalendarDaysWithEvents(
  start: Date,
  end: Date,
  eventsByDate: Record<string, AugmentedUniqueEvents>,
  transcripts: Transcript[],
  selectedMonth: number,
  today: Date
): Promise<MemoryDay[]> {
  // Step 1: Generate all dates in the range
  const dateRange: Date[] = [];
  const current = new Date(start);

  while (current <= end) {
    dateRange.push(new Date(current)); // Push a copy of the date
    current.setDate(current.getDate() + 1);
  }

  // Step 2: Parallelize the computation
  return dateRange.map((date) => {
    return getCalendarDayWithEvents(
      new Date(date),
      eventsByDate,
      transcripts,
      selectedMonth,
      today
    );
  });
}

export const newCalendarEvent = (data: Partial<CalendarEvent | null> = null): CalendarEvent => {
  const { title, description, ...remaining } = data || {};
  return {
    id: undefined,
    providerId: '',
    calendarId: '',
    attendees: [],
    // TODO: Replace with LLM call that comes up with a nice title
    title: (title || 'New Event').slice(0, 100),
    description: (description || '').slice(0, 100),
    end: null,
    location: null,
    notes: null,
    start: null,
    uid: null, // Don't generate random UID for fake calendar events
    ...remaining
  };
};

export const getMemoryTitle = (memory: Memory | undefined) => {
  if (!memory) return 'Untitled Memory';
  if (memory.name) return memory.name;
  if (memory.calendarEvent) return getCalendarTitle(memory.calendarEvent as CalendarEvent);
  return 'Untitled Memory';
};

export const getCalendarTitle = (calendarEvent: CalendarEvent | undefined) => {
  if (!calendarEvent) return 'Untitled Event';
  const diffHours = getDiffHours(calendarEvent);
  if (diffHours == 24) {
    return `[All-day Event]\n${calendarEvent.title || ''}`;
  }
  return calendarEvent.title || '';
};

export const getMeetingType = (uri: string | undefined): string => {
  if (!uri) return '';
  if (uri.includes('zoom.us')) return 'Zoom';
  if (uri.includes('meet.google.com')) return 'Google Meet';
  if (uri.includes('teams.microsoft.com')) return 'Microsoft Teams';
  if (uri.includes('meet.jit.si')) return 'Jitsi';
  return '';
};

export const getMeetingId = (uri: string): string => {
  return uri.split('/').pop() || '';
};

export const getMeetingText = (
  conferenceData: Schema$ConferenceData | null | undefined
): string => {
  if (!conferenceData) return '';
  if (!conferenceData.entryPoints?.[0]?.uri) return '';
  const uri = conferenceData.entryPoints?.[0]?.uri;
  const meetingId = getMeetingId(uri);
  return `Join ${getMeetingType(uri)} ${meetingId}`;
};
